// ======================================================================
// File: src/modules/ppe/pages/procurement/purchaseInvoice/view/PurchaseInvoiceDetail.jsx
// Module: PPE → Procurement → Purchase Invoice
//
// Purpose:
// - Main Purchase Invoice workspace
// - Loads Purchase Invoice detail from backend
// - Handles workflow actions
// - Supports large invoices (500+ / 1000+ lines)
// - Maintains search and pagination state
// - Delegates rendering to child components
//
// Workflow:
//
// DRAFT
//   -> Save
//   -> Submit
//
// SUBMITTED
//   -> Approve
//
// APPROVED
//   -> Post
//
// POSTED
//   -> View Only
//
// ======================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import purchaseInvoiceService from "../services/purchaseInvoiceService";

import PurchaseInvoiceHeaderCard from "./components/PurchaseInvoiceHeaderCard";
import PurchaseInvoiceLineGrid from "./components/PurchaseInvoiceLineGrid";
import PurchaseInvoiceTotalsPanel from "./components/PurchaseInvoiceTotalsPanel";
import PurchaseInvoiceWorkflowPanel from "./components/PurchaseInvoiceWorkflowPanel";
import PurchaseInvoiceAttachments from "./components/PurchaseInvoiceAttachments";
import PurchaseInvoiceAddGRNDrawer from "./components/PurchaseInvoiceAddGRNDrawer";
import PurchaseInvoiceChargeGrid from "./components/PurchaseInvoiceChargeGrid";

import "../purchase-invoice-ui.css";

const PurchaseInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [header, setHeader] = useState({});
  const [lines, setLines] = useState([]);

  const [showGRNDrawer, setShowGRNDrawer] = useState(false);

  const [attachments, setAttachments] = useState([]);
  const [availableCharges, setAvailableCharges] = useState([]);
  const [selectedCharges, setSelectedCharges] = useState([]);


  const [searchText, setSearchText] = useState("");

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [dirty, setDirty] = useState(false);

  const isDraft = header?.status === "DRAFT";

  // ============================================================
  // Load Invoice
  // ============================================================

  const loadInvoice = async () => {
  try {
    setLoading(true);

    const response =
      await purchaseInvoiceService.getPurchaseInvoiceById(
        id
      );

    const payload = response?.data || {};

    console.log(
      "PI DETAIL PAYLOAD",
      payload
    );

    setHeader(
      payload.header || {}
    );

    setLines(
      payload.lines || []
    );

    setAttachments(
      payload.attachments || []
    );

    setSelectedCharges(
      payload.charges || []
    );

    const chargesResponse =
      await purchaseInvoiceService.getAvailableCharges(
        id
      );

    console.log(
      "AVAILABLE CHARGES",
      chargesResponse
    );

    setAvailableCharges(
      chargesResponse?.data || []
    );

  } catch (error) {

    console.error(
      "Failed to load Purchase Invoice",
      error
    );

  } finally {

    setLoading(false);

  }
};

  useEffect(() => {
    loadInvoice();
  }, [id]);

  // ============================================================
  // Unsaved Changes Protection
  // ============================================================

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!dirty) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );
    };
  }, [dirty]);

  // ============================================================
  // Search
  // ============================================================

  const filteredLines = useMemo(() => {
    if (!searchText?.trim()) return lines;

    const keyword = searchText.toLowerCase();

    return lines.filter((line) =>
      JSON.stringify(line)
        .toLowerCase()
        .includes(keyword)
    );
  }, [lines, searchText]);

  // ============================================================
  // Pagination
  // ============================================================

  const totalRecords = filteredLines.length;

  const totalPages =
    Math.ceil(totalRecords / pageSize) || 1;

  const pagedLines = useMemo(() => {
    const start =
      (currentPage - 1) * pageSize;

    const end =
      start + pageSize;

    return filteredLines.slice(start, end);
  }, [
    filteredLines,
    currentPage,
    pageSize
  ]);

  // ============================================================
  // Workflow Actions
  // ============================================================

  const handleSave = async () => {
    try {
      setLoading(true);

      await purchaseInvoiceService.updatePurchaseInvoice(
        id,
        {
          header,
          lines,
          charges: selectedCharges
        }
      );

      setDirty(false);

      await loadInvoice();

    } catch (error) {
      console.error("Save failed", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await purchaseInvoiceService.submitPurchaseInvoice(id);

      setDirty(false);

      await loadInvoice();

    } catch (error) {
      console.error("Submit failed", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setLoading(true);

      await purchaseInvoiceService.approvePurchaseInvoice(id);

      await loadInvoice();

    } catch (error) {
      console.error("Approve failed", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    try {
      setLoading(true);

      await purchaseInvoiceService.postPurchaseInvoice(id);

      await loadInvoice();

    } catch (error) {
      console.error("Post failed", error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Totals
  // ============================================================

const totals = useMemo(() => {

  let materialTotal = 0;
  let materialGST = 0;

  lines.forEach((line) => {

    materialTotal += Number(
      line.line_value ||
      line.material_value ||
      0
    );

    materialGST += Number(
      line.tax_value ||
      line.tax_amount ||
      line.gst_amount ||
      0
    );

  });

  const chargeValue = Number(
    header?.charge_amount || 0
  );

  const chargeGST = Number(
    header?.charge_gst_amount || 0
  );

  const grandTotal = Number(
    header?.net_value || 0
  );

  return {

    materialTotal,

    materialGST,

    chargeValue,

    chargeGST,

    grandTotal

  };

}, [header, lines]);
  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="purchase-invoice-detail-page">

      {loading && (
        <div className="purchase-invoice-loading">
          Loading Purchase Invoice...
        </div>
      )}

      <PurchaseInvoiceHeaderCard
        header={header}
        setHeader={setHeader}
        isDraft={isDraft}
        setDirty={setDirty}
      />
      <div className="pi-lines-toolbar">

        {header.status === "DRAFT" && (
          <button
            className="btn btn-primary"
            onClick={async () => {

              console.log("BUTTON CLICKED");

              try {

                const response =
                  await purchaseInvoiceService.getAvailableGRNs(
                    header.vendor_id
                  );

                console.log(
                  "AVAILABLE GRNS",
                  response
                );

                

                setShowGRNDrawer(true);

              } catch (error) {

                console.error(
                  "GRN LOAD FAILED",
                  error
                );
              }
            }}
          >
            Add GRN Lines
          </button>
        )}

      </div>

      <div className="purchase-invoice-workspace">

        {/* ================================================= */}
        {/* LEFT SIDE                                         */}
        {/* ================================================= */}

        <div className="purchase-invoice-left">

          <PurchaseInvoiceLineGrid
            lines={pagedLines}
            allLines={lines}
            setLines={setLines}
            isDraft={isDraft}
            setDirty={setDirty}
            searchText={searchText}
            setSearchText={setSearchText}
            pageSize={pageSize}
            setPageSize={setPageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalRecords={totalRecords}
            totalPages={totalPages}
          />
          <>
            {isDraft && availableCharges.length > 0 && (
              <button
                className="btn btn-primary mb-2"
                onClick={() => {
                  setSelectedCharges(
                    [...availableCharges]
                  );
                  setDirty(true);
                }}
              >
                Load Available Charges
              </button>
            )}

            
            <PurchaseInvoiceChargeGrid
                charges={selectedCharges}
                setCharges={setSelectedCharges}
                isDraft={header?.status === "DRAFT"}
            />
          </>
          <PurchaseInvoiceAttachments
            purchaseInvoiceId={id}
            attachments={attachments}
            setAttachments={setAttachments}
            isDraft={isDraft}
            setDirty={setDirty}
            loadInvoice={loadInvoice}
          />

        </div>

        {/* ================================================= */}
        {/* RIGHT SIDE                                        */}
        {/* ================================================= */}

        <div className="purchase-invoice-right">

          <PurchaseInvoiceTotalsPanel
            header={header}
            totals={totals}
          />

          <PurchaseInvoiceWorkflowPanel
            status={header?.status}
            dirty={dirty}
            onSave={handleSave}
            onSubmit={handleSubmit}
            onApprove={handleApprove}
            onPost={handlePost}
          />

        </div>

      </div>

     <PurchaseInvoiceAddGRNDrawer
        open={showGRNDrawer}
        onClose={() => setShowGRNDrawer(false)}
        purchaseInvoiceId={id}
        vendorId={header.vendor_id}
        onLinesAdded={(newLines) => {
          setLines((prev) => [
            ...prev,
            ...newLines
          ]);

          setDirty(true);
        }}
      />   
    </div>
  );
};

export default PurchaseInvoiceDetail;